import { useState, useRef, useEffect } from 'react';
import { Save, Eye, EyeOff, Database, Zap, Layers, Scissors, AlertCircle, CheckCircle, Key } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function RAGConfig() {
  const { showToast } = useToast();
  const [showSecrets, setShowSecrets] = useState(false);
  const [vectorDbProvider, setVectorDbProvider] = useState('pinecone');
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const testTimerRef = useRef<number | null>(null);
  const [formData, setFormData] = useState({
    pinecone_api_key: 'pc-xxxxxxxxxxxxxxxx',
    pinecone_environment: 'us-east1-gcp',
    pinecone_index: 'fk-learning-materials',
    embedding_model: 'text-embedding-3-large',
    embedding_dimensions: '1536',
    chunk_size: '512',
    chunk_overlap: '50',
    max_chunks: '5',
    similarity_threshold: '0.75',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    showToast('success', 'Configuration Saved', 'RAG configuration saved successfully');
  };

  const testConnection = () => {
    if (testTimerRef.current) {
      clearTimeout(testTimerRef.current);
    }
    testTimerRef.current = window.setTimeout(() => {
      setTestResult('success');
    }, 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (testTimerRef.current) {
        clearTimeout(testTimerRef.current);
      }
    };
  }, []);

  const vectorDbProviders = [
    {
      id: 'pinecone',
      name: 'Pinecone',
      description: 'Managed vector database with serverless deployment',
      icon: '🌲',
      color: 'emerald',
    },
    {
      id: 'weaviate',
      name: 'Weaviate',
      description: 'Open-source vector search with GraphQL',
      icon: '🔷',
      color: 'blue',
    },
    {
      id: 'pgvector',
      name: 'PostgreSQL',
      description: 'PostgreSQL extension for vector similarity',
      icon: '🐘',
      color: 'indigo',
    },
    {
      id: 'qdrant',
      name: 'Qdrant',
      description: 'High-performance vector similarity engine',
      icon: '⚡',
      color: 'violet',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">RAG & Vector Database</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure Retrieval-Augmented Generation, embeddings, and vector search
          </p>
        </div>
        <button
          onClick={() => setShowSecrets(!showSecrets)}
          className="btn-secondary flex items-center text-sm"
        >
          {showSecrets ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showSecrets ? 'Hide' : 'Show'} Secrets
        </button>
      </div>

      {/* Info Banner */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">What is RAG?</p>
            <p className="text-blue-700">
              Retrieval-Augmented Generation enhances AI responses by retrieving relevant context from your learning materials using vector embeddings. Configure your vector database, embedding model, and chunking strategy below.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vector DB Provider Selection */}
        <div className="card">
          <div className="flex items-start space-x-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Database className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vector Database Provider</h2>
              <p className="text-sm text-gray-600">Choose your vector storage backend</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {vectorDbProviders.map((provider) => (
              <button
                key={provider.id}
                type="button"
                onClick={() => setVectorDbProvider(provider.id)}
                className={`card text-left transition-all hover:shadow-md ${
                  vectorDbProvider === provider.id
                    ? 'ring-2 ring-primary-500 bg-primary-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{provider.icon}</div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{provider.name}</h3>
                <p className="text-xs text-gray-500 line-clamp-2">{provider.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Provider-Specific Configuration */}
        {vectorDbProvider === 'pinecone' && (
          <div className="card">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <span className="text-xl">🌲</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Pinecone Configuration</h2>
                  <p className="text-sm text-gray-600">Connect to your Pinecone index</p>
                </div>
              </div>
              <button
                type="button"
                onClick={testConnection}
                className="btn-secondary flex items-center text-sm"
              >
                {testResult === 'success' ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    Connected
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Test Connection
                  </>
                )}
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showSecrets ? 'text' : 'password'}
                    value={formData.pinecone_api_key}
                    onChange={(e) => setFormData({ ...formData, pinecone_api_key: e.target.value })}
                    className="input font-mono text-sm pr-10"
                    placeholder="pc-..."
                  />
                  <Key className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Get your API key from console.pinecone.io
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Environment
                  </label>
                  <input
                    type="text"
                    value={formData.pinecone_environment}
                    onChange={(e) => setFormData({ ...formData, pinecone_environment: e.target.value })}
                    className="input"
                    placeholder="us-east1-gcp"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cloud region for your index</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Index Name
                  </label>
                  <input
                    type="text"
                    value={formData.pinecone_index}
                    onChange={(e) => setFormData({ ...formData, pinecone_index: e.target.value })}
                    className="input"
                    placeholder="fk-learning-materials"
                  />
                  <p className="text-xs text-gray-500 mt-1">Name of your Pinecone index</p>
                </div>
              </div>
            </div>

            {testResult === 'success' && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 text-sm text-green-800">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Connection successful</span>
                  <span className="text-green-600">• Index is accessible</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Embedding Configuration */}
        <div className="card">
          <div className="flex items-start space-x-3 mb-6">
            <div className="p-2 bg-fk-orange-vibrant100 rounded-lg">
              <Zap className="w-5 h-5 text-#ea7500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Embedding Configuration</h2>
              <p className="text-sm text-gray-600">Configure text-to-vector transformation</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Embedding Model
              </label>
              <select
                value={formData.embedding_model}
                onChange={(e) => setFormData({ ...formData, embedding_model: e.target.value })}
                className="input"
              >
                <option value="text-embedding-3-large">text-embedding-3-large (Best quality)</option>
                <option value="text-embedding-3-small">text-embedding-3-small (Faster)</option>
                <option value="text-embedding-ada-002">text-embedding-ada-002 (Legacy)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                OpenAI model for generating embeddings
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vector Dimensions
              </label>
              <input
                type="number"
                value={formData.embedding_dimensions}
                onChange={(e) => setFormData({ ...formData, embedding_dimensions: e.target.value })}
                className="input"
                placeholder="1536"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must match your vector database index
              </p>
            </div>
          </div>
        </div>

        {/* Chunking Configuration */}
        <div className="card">
          <div className="flex items-start space-x-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Scissors className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Chunking Strategy</h2>
              <p className="text-sm text-gray-600">Configure how documents are split for processing</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chunk Size
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={formData.chunk_size}
                  onChange={(e) => setFormData({ ...formData, chunk_size: e.target.value })}
                  className="input"
                  placeholder="512"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">tokens</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Text segments size</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overlap Size
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={formData.chunk_overlap}
                  onChange={(e) => setFormData({ ...formData, chunk_overlap: e.target.value })}
                  className="input"
                  placeholder="50"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">tokens</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Overlap between chunks</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Chunks
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={formData.max_chunks}
                  onChange={(e) => setFormData({ ...formData, max_chunks: e.target.value })}
                  className="input"
                  placeholder="5"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">per query</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Retrieved per search</p>
            </div>
          </div>
        </div>

        {/* Retrieval Configuration */}
        <div className="card">
          <div className="flex items-start space-x-3 mb-6">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Layers className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Retrieval Settings</h2>
              <p className="text-sm text-gray-600">Control similarity search behavior</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Similarity Threshold
            </label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formData.similarity_threshold}
                  onChange={(e) => setFormData({ ...formData, similarity_threshold: e.target.value })}
                  className="flex-1 mr-4"
                />
                <span className="text-sm font-mono font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded">
                  {formData.similarity_threshold}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Less strict (0.0)</span>
                <span>More strict (1.0)</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Minimum similarity score for chunk retrieval. Higher values return only highly relevant content.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end pt-6 border-t space-x-3">
          <button type="button" className="btn-secondary">
            Reset to Defaults
          </button>
          <button type="submit" className="btn-primary flex items-center">
            <Save className="w-4 h-4 mr-2" />
            Save RAG Configuration
          </button>
        </div>
      </form>
    </div>
  );
}
